import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { TrendingUp, Users, Star, Heart, ArrowRight, CheckCircle, Target } from 'lucide-react';
import { mockData } from '../mock';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Partners = () => {
  return (
    <div className="min-h-screen py-16">
      <SEO
        title="Annonceurs & Partenaires"
        description="Rejoignez les annonceurs qui allient visibilité et impact social. Campagnes publicitaires sur bouteilles d'eau gratuites en Île-de-France."
        path="/partenaires"
      />
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {mockData.partners.title}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            {mockData.partners.subtitle}
          </p>
        </div>
      </section>

      {/* Stats décisives */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Des résultats qui parlent d'eux-mêmes
            </h2>
            <p className="text-xl text-gray-600">
              Comparé au publipostage traditionnel, nos résultats sont exceptionnels
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-8 text-center border-0 shadow-xl bg-gradient-to-br from-sky-500 to-sky-600 text-white">
              <CardContent className="p-0">
                <div className="text-4xl font-bold mb-3">10x</div>
                <div className="text-lg font-medium mb-2">Plus d'impressions</div>
                <div className="text-sm opacity-90">que le publipostage direct</div>
              </CardContent>
            </Card>
            
            <Card className="p-8 text-center border-0 shadow-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
              <CardContent className="p-0">
                <div className="text-4xl font-bold mb-3">2,50€</div>
                <div className="text-lg font-medium mb-2">Moins cher</div>
                <div className="text-sm opacity-90">par 10 impressions vs publipostage</div>
              </CardContent>
            </Card>
            
            <Card className="p-8 text-center border-0 shadow-xl bg-gradient-to-br from-sky-500 to-emerald-500 text-white">
              <CardContent className="p-0">
                <div className="text-4xl font-bold mb-3">29%</div>
                <div className="text-lg font-medium mb-2">ROI garanti</div>
                <div className="text-sm opacity-90">retour sur investissement prouvé</div>
              </CardContent>
            </Card>
            
            <Card className="p-8 text-center border-0 shadow-xl bg-gradient-to-br from-emerald-500 to-sky-500 text-white">
              <CardContent className="p-0">
                <div className="text-4xl font-bold mb-3">♾️</div>
                <div className="text-lg font-medium mb-2">Impact social</div>
                <div className="text-sm opacity-90">sauver des vies + aider la planète</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi devenir partenaire ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rejoignez un mouvement qui transforme votre investissement publicitaire en impact social mesurable.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: TrendingUp,
                title: 'Impact RSE',
                description: 'Améliorez votre score RSE avec un impact social quantifiable',
                color: 'sky'
              },
              {
                icon: Users,
                title: 'Audience Engagée',
                description: 'Touchez une communauté consciente et engagée pour la solidarité',
                color: 'emerald'
              },
              {
                icon: Star,
                title: 'Image de Marque',
                description: 'Associez votre marque à des valeurs positives et inspirantes',
                color: 'sky'
              },
              {
                icon: Heart,
                title: 'Transparence',
                description: 'Recevez des rapports détaillés sur l\'impact de votre contribution',
                color: 'emerald'
              }
            ].map((benefit, index) => (
              <Card key={index} className="p-6 border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardContent className="p-0 text-center space-y-4">
                  <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-r from-${benefit.color}-400 to-${benefit.color === 'sky' ? 'emerald' : 'sky'}-400 rounded-xl mx-auto group-hover:scale-110 transition-transform duration-200`}>
                    <benefit.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Benefits Detail */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Les avantages concrets de notre partenariat
              </h2>
              <div className="space-y-6">
                {mockData.partners.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-emerald-500 mt-1" />
                    </div>
                    <div>
                      <p className="text-lg text-gray-700">{benefit}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-6">
              <Card className="p-6 border-0 shadow-xl bg-white">
                <CardContent className="p-0">
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl mx-auto">
                      <Target className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">ROI Social</h3>
                    <p className="text-gray-600">
                      Chaque euro investi génère un impact social mesurable et durable.
                    </p>
                    <div className="bg-gradient-to-r from-sky-100 to-emerald-100 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-sky-600">5x</div>
                      <div className="text-sm text-gray-600">Retour sur investissement social</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="p-6 border-0 shadow-xl bg-white">
                <CardContent className="p-0">
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-400 to-sky-400 rounded-xl mx-auto">
                      <Users className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Audience</h3>
                    <p className="text-gray-600">
                      Accédez à une communauté de plus de 100k consommateurs engagés.
                    </p>
                    <div className="bg-gradient-to-r from-emerald-100 to-sky-100 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600">92%</div>
                      <div className="text-sm text-gray-600">Taux de satisfaction client</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials from potential partners */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir Eau Gratuite ?
            </h2>
            <p className="text-xl text-gray-600">
              Une plateforme publicitaire innovante avec impact social garanti
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: "Distribution Île-de-France",
                description: "Vos bouteilles publicitaires atteignent des milliers de franciliens quotidiennement",
                stat: "50k+",
                statLabel: "Bouteilles/jour"
              },
              {
                title: "QR Code Interactif", 
                description: "Chaque bouteille inclut un QR code pour rediriger vers votre site web",
                stat: "100%",
                statLabel: "Traçabilité"
              },
              {
                title: "RSE Authentique",
                description: "Vos campagnes financent directement des puits d'eau avec l'Association Ulker's",
                stat: "50+",
                statLabel: "Puits construits"
              }
            ].map((benefit, index) => (
              <Card key={index} className="p-8 border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
                <CardContent className="p-0 text-center space-y-4">
                  <div className="bg-gradient-to-br from-sky-50 to-emerald-50 p-6 rounded-lg group-hover:from-sky-100 group-hover:to-emerald-100 transition-all duration-300">
                    <div className="text-3xl font-bold text-sky-600 mb-2">{benefit.stat}</div>
                    <div className="text-sm text-gray-600 mb-4">{benefit.statLabel}</div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Packages */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-sky-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Nos formules de partenariat
            </h2>
            <p className="text-xl text-gray-600">
              Choisissez la formule qui correspond à vos objectifs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
            {[
              {
                name: 'Starter',
                price: '10 000€',
                period: '',
                features: [
                  '15 000 bouteilles d\'eau 33 cl distribuées',
                  'Publicité sur les bouteilles',
                  'Support client dédié'
                ],
                color: 'sky',
                popular: false
              },
              {
                name: 'Premium',
                price: '25 000€',
                period: '',
                features: [
                  '50 000 bouteilles d\'eau 33 cl distribuées',
                  'Publicité sur les bouteilles',
                  'Événements exclusifs',
                  'Support client dédié'
                ],
                color: 'emerald',
                popular: true
              },
              {
                name: 'Enterprise',
                price: 'Sur mesure',
                period: '',
                features: [
                  'Événements sur mesure',
                  'Account manager dédié',
                  'Co-branding avec autres marques partenaires',
                  'Distribution programmée sur plusieurs dates'
                ],
                color: 'sky',
                popular: false
              }
            ].map((plan, index) => (
              <Card key={index} className={`p-8 border-0 shadow-xl relative ${plan.popular ? 'ring-2 ring-emerald-400 mt-6' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-emerald-500 to-sky-500 text-white px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap shadow-lg">
                      Plus populaire
                    </span>
                  </div>
                )}
                <CardContent className="p-0 space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="space-y-1">
                      <div className={`text-4xl font-bold text-${plan.color}-600`}>{plan.price}</div>
                      {plan.period && <div className="text-gray-600">{plan.period}</div>}
                    </div>
                  </div>
                  
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className={`h-5 w-5 text-${plan.color}-500`} />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/contact">
                    <Button 
                      className={`w-full ${plan.popular 
                        ? 'bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-emerald-600 hover:to-sky-600 text-white' 
                        : 'border-2 border-sky-200 text-sky-600 hover:bg-sky-50'
                      } px-6 py-3 rounded-xl font-semibold transition-all duration-200`}
                      variant={plan.popular ? 'default' : 'outline'}
                    >
                      {plan.name === 'Enterprise' ? 'Demander un devis' : 'Choisir ce plan'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-sky-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              Prêt à rejoindre notre mission ?
            </h2>
            <p className="text-xl text-sky-100 leading-relaxed">
              Contactez-nous pour discuter de votre projet de partenariat et découvrir comment votre marque peut faire la différence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-white text-sky-600 hover:bg-sky-50 px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group"
                >
                  Lancer ma campagne
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-sky-600 px-8 py-4 rounded-xl font-semibold transition-all duration-200"
                >
                  Obtenir un devis personnalisé
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;